// LoginPage.jsx
// Phase 1: Username + password login.
// Step structure is ready for Phase 2 MFA.

import React, { useState, useRef, useEffect } from 'react';
import { useAuth, AUTH_STEPS } from '../context/AuthContext';
import logoSrc from '../assets/Main-logo-UI.png';
import './LoginPage.css';
import LegalLinks from '../legal/LegalLinks';

// Microsoft sign-in — ENABLED 5 Sep 2026, once the backend could actually serve it.
//
// It was hidden while `/auth/v1/settings` reported `azure: false`: the button would
// redirect to Supabase, which had no Azure client id, and return an error. A control
// that always fails is worse than one that is absent.
//
// Both halves are now in place, verified rather than assumed:
//   - Supabase reports `azure: true`.
//   - Entra has a SEPARATE registration, `DigitalMe Sign-in`
//     (32e06c76-…), audience AzureADandPersonalMicrosoftAccount, with
//     https://ftgeqwidawuotokqufpx.supabase.co/auth/v1/callback among its redirect URIs.
//
// That separation is deliberate and worth preserving. `DigitalMe Mail Connector`
// (1aba2ebf-…) is a different app for a different job: it holds Mail/Calendar/Files
// scopes, its credentials live in VERCEL env vars, and it redirects to the app's own
// /api/auth/microsoft/callback. Sign-in credentials live in the SUPABASE dashboard and
// redirect to Supabase. Merging them would mean rotating one secret breaks both.
//
// Deliberately a constant rather than an env var: it is a fact about the backend's
// configuration, not a per-deployment preference, and a REACT_APP_* var would have to
// be set identically in every environment to mean the same thing.
const SHOW_MICROSOFT_SIGNIN = true;

export default function LoginPage() {
  const { login, signUp, signInWithProvider, requestPasswordReset, verifyMfa, step } = useAuth();
  const [showForgot, setShowForgot] = React.useState(false);
  const [showSignUp, setShowSignUp] = React.useState(false);

  return (
    <div className="lp-root">
      {/* Animated background */}
      <div className="lp-bg">
        <div className="lp-bg-orb lp-bg-orb--1" />
        <div className="lp-bg-orb lp-bg-orb--2" />
        <div className="lp-bg-orb lp-bg-orb--3" />
      </div>

      {/* Centered card */}
      <div className="lp-card">
        {/* Logo + brand */}
        <div className="lp-brand">
          <div className="lp-logo-wrap">
            <img src={logoSrc} alt="Digital Me" className="lp-logo" />
          </div>
          <h1 className="lp-brand-name">Digital Me</h1>
          <p className="lp-brand-tag">Intelligence. Shaped Around You.</p>
        </div>

        {/* Step router */}
        {showSignUp
          ? <SignUpForm onSignUp={signUp} onBack={() => setShowSignUp(false)} />
          : showForgot
            ? <ForgotPasswordForm onBack={() => setShowForgot(false)} onReset={requestPasswordReset} />
            : step === AUTH_STEPS.CREDENTIALS
              ? <CredentialsForm
                  onLogin={login}
                  onOAuth={signInWithProvider}
                  onForgot={() => setShowForgot(true)}
                  onSignUp={() => setShowSignUp(true)}
                />
              : <MfaForm onVerify={verifyMfa} />
        }
      </div>

      {/* Footer */}
      <div className="lp-footer">
        © {new Date().getFullYear()} Digital Me · All rights reserved
        <LegalLinks className="lp-legal" />
      </div>
    </div>
  );
}

// -- Credentials form ------------------------------------------
function CredentialsForm({ onLogin, onOAuth, onForgot, onSignUp }) {
  const [username, setUsername]     = useState('');
  const [password, setPassword]     = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPass, setShowPass]     = useState(false);
  const [error, setError]           = useState('');
  const [loading, setLoading]       = useState(false);
  const [shake, setShake]           = useState(false);
  const [oauthLoading, setOauthLoading] = useState(null);
  const usernameRef                 = useRef(null);

  useEffect(() => { usernameRef.current?.focus(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password) {
      setError('Please enter your email and password.');
      triggerShake();
      return;
    }

    setLoading(true);
    setError('');

    const result = await onLogin({ username, password, rememberMe });

    if (!result.success) {
      setError(result.error || 'Sign in failed. Please try again.');
      setPassword('');
      triggerShake();
    }

    setLoading(false);
  };

  const triggerShake = () => {
    setShake(true);
    setTimeout(() => setShake(false), 500);
  };

  // On success the browser navigates to the provider, so `loading` is never cleared on
  // the happy path — the page is gone. It is cleared on failure so the form is usable
  // again.
  const handleOAuth = async (provider) => {
    setError('');
    setOauthLoading(provider);
    const result = await onOAuth(provider);
    if (!result.success) {
      setError(result.error || 'Sign in failed. Please try again.');
      setOauthLoading(null);
      triggerShake();
    }
  };

  return (
    <form
      className={`lp-form ${shake ? 'lp-form--shake' : ''}`}
      onSubmit={handleSubmit}
      noValidate
    >
      <div className="lp-form-title">Sign in to your workspace</div>

      {/* Error */}
      {error && (
        <div className="lp-error">
          <span className="lp-error-icon">⚠</span>
          {error}
        </div>
      )}

      {/* ── Single sign-on ──────────────────────────────────────────────
          Placed ABOVE the password form deliberately: SSO is the path new users
          take, and burying it under a form they have no credentials for is how
          people end up requesting a password reset for an account that has none. */}
      <div className="lp-sso">
        <button
          type="button"
          className="lp-sso-btn"
          onClick={() => handleOAuth('google')}
          disabled={loading || !!oauthLoading}
        >
          <svg className="lp-sso-icon" viewBox="0 0 18 18" aria-hidden="true">
            <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z"/>
            <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z"/>
            <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z"/>
            <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.9 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z"/>
          </svg>
          {oauthLoading === 'google' ? 'Redirecting…' : 'Continue with Google'}
        </button>

        {SHOW_MICROSOFT_SIGNIN && (
          <button
            type="button"
            className="lp-sso-btn"
            onClick={() => handleOAuth('azure')}
            disabled={loading || !!oauthLoading}
          >
            <svg className="lp-sso-icon" viewBox="0 0 18 18" aria-hidden="true">
              <path fill="#F25022" d="M0 0h8.5v8.5H0z"/>
              <path fill="#7FBA00" d="M9.5 0H18v8.5H9.5z"/>
              <path fill="#00A4EF" d="M0 9.5h8.5V18H0z"/>
              <path fill="#FFB900" d="M9.5 9.5H18V18H9.5z"/>
            </svg>
            {oauthLoading === 'azure' ? 'Redirecting…' : 'Continue with Microsoft'}
          </button>
        )}
      </div>

      <div className="lp-divider"><span>or sign in with email</span></div>

      {/* Email */}
      <div className="lp-field">
        <label className="lp-label" htmlFor="dm-username">Email</label>
        <input
          id="dm-username"
          ref={usernameRef}
          className="lp-input"
          type="email"
          value={username}
          onChange={e => setUsername(e.target.value)}
          placeholder="Enter your email"
          autoComplete="email"
          disabled={loading}
          spellCheck={false}
        />
      </div>

      {/* Password */}
      <div className="lp-field">
        <div className="lp-label-row">
          <label className="lp-label" htmlFor="dm-password">Password</label>
          <button
            type="button"
            className="lp-forgot"
            onClick={onForgot}
          >
            Forgot password?
          </button>
        </div>
        <div className="lp-input-wrap">
          <input
            id="dm-password"
            className="lp-input lp-input--pass"
            type={showPass ? 'text' : 'password'}
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="Enter your password"
            autoComplete="current-password"
            disabled={loading}
          />
          <button
            type="button"
            className="lp-show-pass"
            onClick={() => setShowPass(s => !s)}
            tabIndex={-1}
            aria-label={showPass ? 'Hide password' : 'Show password'}
          >
            {showPass ? '🙈' : '👁'}
          </button>
        </div>
      </div>

      {/* Remember me */}
      <label className="lp-remember">
        <input
          type="checkbox"
          checked={rememberMe}
          onChange={e => setRememberMe(e.target.checked)}
          className="lp-checkbox"
          disabled={loading}
        />
        <span>Keep me signed in for 14 days</span>
      </label>

      {/* Submit */}
      <button
        type="submit"
        className="lp-btn-submit"
        disabled={loading || !username.trim() || !password}
      >
        {loading ? (
          <span className="lp-btn-loading">
            <span className="lp-spinner" />
            Signing in…
          </span>
        ) : (
          'Sign In →'
        )}
      </button>

      {/* P0.13 task 13.4 — this fired alert('Subscription sign-up coming soon.') until
          13 Sep 2026. AuthContext had no signUp() at all, so a new user who typed an
          email and password instead got "Invalid email or password." — a message that
          states the opposite of their situation. */}
      <div className="lp-signup-note">
        Don't have an account?{' '}
        <button type="button" className="lp-signup-link" onClick={onSignUp}>
          Get started
        </button>
      </div>
    </form>
  );
}


// -- Sign-up form (P0.13 task 13.4) ----------------------------
//
// Deliberately the same shape as the credentials form, because the outcome is the same
// shape: an account that exists and cannot see anything until an administrator approves
// it. Sign-up is open; ACCESS is not.
//
// The name is collected here and passed as full_name in options.data, which lands in
// raw_user_meta_data — exactly where Google and Microsoft put theirs. Migration 0020's
// trigger reads that one key regardless of provider, so this form adds no second place
// for a name to live and no second code path to keep in step.
function SignUpForm({ onSignUp, onBack }) {
  const [fullName, setFullName] = useState('');
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [done, setDone]         = useState(null);   // { needsConfirmation }
  const nameRef                 = useRef(null);

  useEffect(() => { nameRef.current?.focus(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      setError('Please enter your email and a password.');
      return;
    }
    setLoading(true);
    setError('');
    const result = await onSignUp({ email, password, fullName });
    setLoading(false);
    if (!result.success) {
      setError(result.error || 'Could not create the account. Please try again.');
      return;
    }
    setDone({ needsConfirmation: result.needsConfirmation });
  };

  if (done) {
    // Two different waits, and saying the wrong one is worse than saying nothing. With
    // confirmation ON, Supabase returns no session and the account cannot sign in until
    // the link is clicked — telling that person "the team is reviewing you" is false and
    // stops them checking their inbox. With it OFF, they are signed in already and
    // AuthGate takes over, so this branch only needs to cover the confirmation case.
    return (
      <div className="lp-form">
        <div className="lp-form-title">
          {done.needsConfirmation ? 'Confirm your email' : 'Account created'}
        </div>
        <p className="lp-mfa-desc" style={{ textAlign: 'center', marginBottom: 24 }}>
          {done.needsConfirmation ? (
            <>We've sent a confirmation link to <strong>{email}</strong>. Click it to
            activate your account — then an administrator will approve your access.</>
          ) : (
            <>Your account is ready. An administrator needs to approve your access
            before you can use DigitalMe.</>
          )}
        </p>
        <button type="button" className="lp-btn-submit" onClick={onBack}>
          Back to Sign In
        </button>
      </div>
    );
  }

  return (
    <form className="lp-form" onSubmit={handleSubmit} noValidate>
      <div className="lp-form-title">Create your account</div>

      {error && (
        <div className="lp-error">
          <span className="lp-error-icon">⚠</span>
          {error}
        </div>
      )}

      <div className="lp-field">
        <label className="lp-label" htmlFor="dm-signup-name">Full name</label>
        <input
          id="dm-signup-name"
          ref={nameRef}
          className="lp-input"
          type="text"
          value={fullName}
          onChange={e => setFullName(e.target.value)}
          placeholder="Jim Smith"
          autoComplete="name"
          disabled={loading}
        />
      </div>

      <div className="lp-field">
        <label className="lp-label" htmlFor="dm-signup-email">Email</label>
        <input
          id="dm-signup-email"
          className="lp-input"
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="your@email.com"
          autoComplete="email"
          disabled={loading}
          spellCheck={false}
        />
      </div>

      <div className="lp-field">
        <label className="lp-label" htmlFor="dm-signup-password">Password</label>
        <input
          id="dm-signup-password"
          className="lp-input"
          type="password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          placeholder="Choose a password"
          autoComplete="new-password"
          disabled={loading}
        />
      </div>

      <button
        type="submit"
        className="lp-btn-submit"
        disabled={loading || !email.trim() || !password}
      >
        {loading
          ? <span className="lp-btn-loading"><span className="lp-spinner" />Creating…</span>
          : 'Create Account →'}
      </button>

      {/* Acceptance itself is recorded after the first sign-in (AcceptTermsPage), one
          path for email and OAuth sign-ups alike. This says so before they commit. */}
      <div className="lp-signup-note">
        You'll be asked to accept our{' '}
        <a className="lp-signup-link" href="/#terms" target="_blank" rel="noopener noreferrer">Terms</a>
        {' '}and{' '}
        <a className="lp-signup-link" href="/#privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
        {' '}when you first sign in.
      </div>

      <div className="lp-signup-note">
        Already have an account?{' '}
        <button type="button" className="lp-signup-link" onClick={onBack}>
          Sign in
        </button>
      </div>
    </form>
  );
}

// -- Forgot Password form --------------------------------------
function ForgotPasswordForm({ onBack, onReset }) {
  const [email, setEmail]       = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  // P0.22 — this used to await a 1200ms timer and then claim, unconditionally, that
  // reset instructions had been sent. No Supabase call was ever made and no mail was
  // ever sent. That is worse than an unimplemented feature: the user stops chasing it
  // and waits for an email that cannot arrive.
  //
  // The success copy below is deliberately neutral ("if an account exists") and must
  // STAY neutral. Branching it on whether the address is registered would turn this
  // form into an account-enumeration oracle — which is also why a failure here reports
  // a transport error rather than anything about the address.
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    setError('');
    const result = await onReset(email);
    setLoading(false);
    if (!result.success) {
      setError(result.error || 'Could not send the reset email. Please try again.');
      return;
    }
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="lp-form">
        <div className="lp-form-title">Check your email</div>
        <p className="lp-mfa-desc" style={{textAlign:'center', color:'#4ade80', marginBottom:24}}>
          ✅ If an account exists for <strong>{email}</strong>, password reset instructions have been sent.
        </p>
        <button type="button" className="lp-btn-submit" onClick={onBack}>
          Back to Sign In
        </button>
      </div>
    );
  }

  return (
    <form className="lp-form" onSubmit={handleSubmit} noValidate>
      <div className="lp-form-title">Reset your password</div>
      <p className="lp-mfa-desc">Enter your email address and we'll send you reset instructions.</p>

      {error && (
        <div className="lp-error">
          <span className="lp-error-icon">⚠</span>
          {error}
        </div>
      )}

      <div className="lp-field">
        <label className="lp-label" htmlFor="dm-reset-email">Email address</label>
        <input
          id="dm-reset-email"
          className="lp-input"
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="your@email.com"
          autoComplete="email"
          disabled={loading}
          autoFocus
        />
      </div>
      <button
        type="submit"
        className="lp-btn-submit"
        disabled={loading || !email.trim()}
      >
        {loading
          ? <span className="lp-btn-loading"><span className="lp-spinner" />Sending…</span>
          : 'Send Reset Link →'}
      </button>
      <div className="lp-signup-note">
        <button type="button" className="lp-signup-link" onClick={onBack}>
          ← Back to Sign In
        </button>
      </div>
    </form>
  );
}

// -- MFA form -- Phase 2 placeholder ---------------------------
function MfaForm({ onVerify }) {
  const [code, setCode]     = useState('');
  const [error, setError]   = useState('');
  const [loading, setLoading] = useState(false);
  const inputs              = useRef([]);

  const handleDigit = (i, val) => {
    const digits = code.split('');
    digits[i] = val.slice(-1);
    const next = digits.join('');
    setCode(next);
    if (val && i < 5) inputs.current[i + 1]?.focus();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (code.length < 6) { setError('Enter the 6-digit code.'); return; }
    setLoading(true);
    const result = await onVerify({ code });
    if (!result.success) setError(result.error || 'Invalid code.');
    setLoading(false);
  };

  return (
    <form className="lp-form" onSubmit={handleSubmit}>
      <div className="lp-form-title">Two-factor verification</div>
      <p className="lp-mfa-desc">Enter the 6-digit code from your authenticator app.</p>

      {error && <div className="lp-error"><span className="lp-error-icon">⚠</span>{error}</div>}

      <div className="lp-mfa-digits">
        {Array.from({ length: 6 }).map((_, i) => (
          <input
            key={i}
            ref={el => inputs.current[i] = el}
            className="lp-mfa-digit"
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={code[i] || ''}
            onChange={e => handleDigit(i, e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Backspace' && !code[i] && i > 0) inputs.current[i - 1]?.focus();
            }}
            disabled={loading}
          />
        ))}
      </div>

      <button type="submit" className="lp-btn-submit" disabled={loading || code.length < 6}>
        {loading ? <span className="lp-btn-loading"><span className="lp-spinner" />Verifying…</span> : 'Verify →'}
      </button>
    </form>
  );
}
